import React, { FC, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "../../../utils/api";
import ApplicationDetailsModal from "../../../components/ApplicationDetailsModal/ApplicationDetailsModal";
import ConfirmModal from "../../../components/ConfirmModal";

interface Application {
  _id: string;
  programId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  education: string;
  experience: string;
  motivation: string;
  expectations: string;
  heardFrom: string;
  status: "pending" | "approved" | "rejected";
  createdAt: string;
  updatedAt: string;
}

const Applications: FC = () => {
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [applicationToUpdate, setApplicationToUpdate] = useState<{id: string, status: string} | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  const queryClient = useQueryClient();

  const { data: applications, isLoading } = useQuery<Application[]>({
    queryKey: ["program-applications"],
    queryFn: () =>
      api.get("/startup-school/admin/applications").then((res) => res.data),
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string, status: string }) =>
      api.put(`/startup-school/admin/applications/${id}/status`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["program-applications"] });
      queryClient.invalidateQueries({ queryKey: ["programs"] });
      setShowConfirmModal(false);
      setApplicationToUpdate(null);
    },
    onError: (error) => {
      console.error("Failed to update application status:", error);
    },
  });

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-yellow-100 text-yellow-800";
    }
  };

  const handleApprove = (id: string) => {
    setApplicationToUpdate({ id, status: "approved" });
    setShowConfirmModal(true);
  };

  const handleReject = (id: string) => {
    setApplicationToUpdate({ id, status: "rejected" });
    setShowConfirmModal(true);
  };

  const handleViewDetails = (application: Application) => {
    setSelectedApplication(application);
    setShowDetailsModal(true);
  };

  const handleConfirmStatusUpdate = () => {
    if (applicationToUpdate) {
      updateStatusMutation.mutate(applicationToUpdate);
    }
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedApplication(null);
  };

  const handleCloseConfirm = () => {
    setShowConfirmModal(false);
    setApplicationToUpdate(null);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="sm:flex sm:items-center mb-6">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-bold text-gray-900">
            Program Applications
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            View and manage all program applications
          </p>
        </div>
      </div>

      <div className="shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
                Applicant
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Contact
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Status
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Applied On
              </th>
              <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {applications?.map((application) => {
              console.log("applicants:::", application);
              return (
                <tr key={application._id}>
                  <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm">
                    <div className="font-medium text-gray-900">
                      {application.firstName} {application.lastName}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <div className="text-gray-900">{application.email}</div>
                    <div className="text-gray-500">{application.phone}</div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <span
                      className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${getStatusBadgeColor(
                        application.status
                      )}`}
                    >
                      {application.status.charAt(0).toUpperCase() +
                        application.status.slice(1)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                    {new Date(application.createdAt).toLocaleDateString()}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <div className="flex space-x-3">
                      {application.status === "pending" && (
                        <>
                          <button
                            onClick={() => handleApprove(application._id)}
                            disabled={updateStatusMutation.isPending}
                            className="text-green-600 hover:text-green-900 disabled:opacity-50"
                          >
                            {updateStatusMutation.isPending ? "Processing..." : "Approve"}
                          </button>
                          <button
                            onClick={() => handleReject(application._id)}
                            disabled={updateStatusMutation.isPending}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          >
                            {updateStatusMutation.isPending ? "Processing..." : "Reject"}
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleViewDetails(application)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        View Details
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Application Details Modal */}
      <ApplicationDetailsModal
        isOpen={showDetailsModal}
        onClose={handleCloseDetails}
        application={selectedApplication}
      />

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={showConfirmModal}
        title={`${applicationToUpdate?.status === "approved" ? "Approve" : "Reject"} Application`}
        message={`Are you sure you want to ${applicationToUpdate?.status === "approved" ? "approve" : "reject"} this application?`}
        onConfirm={handleConfirmStatusUpdate}
        onCancel={handleCloseConfirm}
        confirmText={applicationToUpdate?.status === "approved" ? "Yes, Approve" : "Yes, Reject"}
        cancelText="No, Cancel"
        confirmButtonClass={applicationToUpdate?.status === "approved" 
          ? "px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          : "px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
        }
      />
    </div>
  );
};

export default Applications;
